import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { Inventory, InventoryPayload, SelectOption } from "../types";

interface InventoryFormDialogProps {
  open: boolean;
  loading?: boolean;
  inventory?: Inventory | null;
  spareParts: SelectOption[];
  warehouses: SelectOption[];
  onClose: () => void;
  onSubmit: (payload: InventoryPayload) => Promise<void>;
}

const defaultValues: InventoryPayload = {
  sparePartId: "",
  warehouseId: "",
  minimumStock: 0,
  maximumStock: 0,
  reorderLevel: 10,
  remarks: "",
};

const toNumberOrUndefined = (value?: number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const emptyToUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const InventoryFormDialog = ({
  open,
  loading = false,
  inventory,
  spareParts,
  warehouses,
  onClose,
  onSubmit,
}: InventoryFormDialogProps) => {
  const { control, reset, handleSubmit } = useForm<InventoryPayload>({
    defaultValues,
  });

  useEffect(() => {
    if (inventory) {
      reset({
        sparePartId: inventory.sparePartId,
        warehouseId: inventory.warehouseId,
        minimumStock: inventory.minimumStock,
        maximumStock: inventory.maximumStock || 0,
        reorderLevel: inventory.reorderLevel,
        remarks: inventory.remarks || "",
      });
    } else {
      reset(defaultValues);
    }
  }, [inventory, reset]);

  const submit = (values: InventoryPayload) =>
    onSubmit({
      sparePartId: values.sparePartId,
      warehouseId: values.warehouseId,
      minimumStock: toNumberOrUndefined(values.minimumStock),
      maximumStock: toNumberOrUndefined(values.maximumStock),
      reorderLevel: toNumberOrUndefined(values.reorderLevel),
      remarks: emptyToUndefined(values.remarks),
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {inventory ? "Edit Inventory Stock" : "Add Inventory Stock"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="sparePartId"
                control={control}
                rules={{ required: "Spare Part is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    disabled={Boolean(inventory)}
                    label="Spare Part"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      (spareParts.length ? "" : "Create a spare part first.")
                    }
                  >
                    {spareParts.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="warehouseId"
                control={control}
                rules={{ required: "Hub is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    disabled={Boolean(inventory)}
                    label="Hub"
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      (warehouses.length ? "" : "Vehicle Service hubs are not loaded.")
                    }
                  >
                    {warehouses.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="minimumStock"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth type="number" label="Minimum Stock" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="maximumStock"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth type="number" label="Maximum Stock" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="reorderLevel"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth type="number" label="Reorder Level" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth multiline minRows={3} label="Remarks" />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit(submit)} disabled={loading}>
          {loading ? "Saving..." : inventory ? "Update Stock" : "Create Stock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryFormDialog;
